import Vue, { VueConstructor } from "vue";
import '../styles/all.scss';
import VopsComponents from "../components";
import VopsDirectives from "../directives";
import rules, {Rules} from "./rules";

export interface VopsUIObject {
	options: VopsUIUseOptions,
	labels?: VopsUIUseLabels
}

export interface VopsUIUseOptions {
	currencySymbol?: () => string;
	currencyDecimalDigits?: () => number;
	currencyGroupSeparator?: () => string;
	currencyDecimalSeparator?: () => string;
	numberGroupSeparator?: () => string;
	numberDecimalSeparator?: () => string;
	startDayOfWeek?: () => number;
}

export interface VopsUIUseLabels {
	save?: () => string;
	close?: () => string;
	saveAndClose?: () => string;
	slider?: ()=> string;
	page?: ()=>string;
	cancel?: () => string;
	remove?: () => string;
	loading?: () => string;
	continue?: () => string;
	newText?: () => string;
	filedrop?: {
		addFiles?: () => string;
		fileSizeExceeded?: (value: string) => string;
		invalidFileExtension?: (value: string) => string;
		restrictedFileExtension?: () => string;
	}
	combobox?: {
		valueDoesNotExist?: (value: string) => string;
		createValueQuotes?: (value: string) => string;
		valueWillBeCreated?: (value: string) => string;
	},
	idle?: () => string;
	idleCountdown?: (value: string) => string;
	fieldRequired?: () => string;
	invalidTime?: () => string;
	invalidDate?: () => string;
	invalidCurrency?: () => string;
	emailMustBeValid?: () => string;
	urlMustBeValid?: () => string;
	valueMustBeMin?: (value: string) => string;
	valueMustBeMax?: (value: string) => string;
	lengthUnderMin?: (value: string) => string;
	lengthExceedsMax?: (value: string) => string;
	invalidNumber?: () => string;
	invalidWholeNumber?: () => string;
	valueAlreadyExists?: (value) => string;
}

declare module 'vue/types/vue' {
	export interface Vue {
		$vopsui: VopsUIObject
	}
}

function compId(name: string, prefix?: string) {
	const matches = name.match(/([A-Z][a-z0-9]*)/g);
	const arr = prefix ? [prefix, ...matches] : matches;
	const id = arr.join("-").toLowerCase();
	return id;
}

function install(Vue: VueConstructor) {
	if (install.installed)
		return;

	install.installed = true;

	Object.keys(VopsComponents).forEach(key => {
		Vue.component(compId(key), VopsComponents[key]);
	});

	Object.keys(VopsDirectives).forEach(key => {
		Vue.directive(key, VopsDirectives[key]);
	});

	Vue.mixin({
		beforeCreate() {
			const options = this.$options as any;

			if (options.vopsui && !Vue.prototype.hasOwnProperty('$vopsui')) {
				Vue.prototype.$vopsui = Vue.observable(options.vopsui);
				Vue.prototype.$rules = rules;
			}
		}
	});
}

install.installed = false;

declare module 'vue/types/options' {
	export interface ComponentOptions<
		V extends Vue,
		Data = DefaultData<V>,
		Methods = DefaultMethods<V>,
		Computed = DefaultComputed,
		PropsDef = PropsDefinition<DefaultProps>,
		Props = DefaultProps> {
		vopsui?: any
	}
}

declare module 'vue/types/vue' {
	export interface Vue {
		$vopsui: VopsUIObject;
		$rules: Rules;
	}
}

export default class VopsUI implements VopsUIObject {
	static install = install;
	static installed: boolean = false;

	constructor(presets: Partial<VopsUIObject> = {}) {
		const options = presets.options || {};
		this.options = {
			...this.options,
			...options
		};

		const labels = presets.labels || {};
		this.labels = {
			...this.labels,
			...labels
		};
	}

	options: VopsUIUseOptions = {
		currencySymbol: () => "$",
		currencyDecimalDigits: () => 2,
		currencyGroupSeparator: () => ",",
		currencyDecimalSeparator: () => ".",
		numberGroupSeparator: () => ",",
		numberDecimalSeparator: () => ".",
		startDayOfWeek: () => 0
	};

	labels: VopsUIUseLabels = {
		save: () => "Save",
		saveAndClose: () => "Save And Close",
		slider: ()=> 'Slider',
		page: ()=> 'Page',
		close: ()=> "Close",
		cancel: () => "Cancel",
		remove: () => "Remove",
		loading: () => "Loading",
		newText: () => "New",
		filedrop: {
			addFiles: () => "Add files",
			fileSizeExceeded: value => `File size must be less than ${value}mb`,
			invalidFileExtension: value => `File extension must be ${value}`,
			restrictedFileExtension: () =>`Uploaded File extension is restricted`
		},
		combobox: {
			createValueQuotes: value => `Create '${value}'`,
			valueDoesNotExist: value => `${value} does not exist`,
			valueWillBeCreated: value => `'${value}' will be created`
		},
		fieldRequired: () => 'This field is required',
		invalidTime: () => 'Invalid time',
		invalidDate: () => 'Invalid date',
		invalidCurrency: () => "Invalid currency format",
		emailMustBeValid: () => "Email must be valid",
		urlMustBeValid: () => "URL must be valid",
		valueMustBeMin: value => `Value must be greater than or equal to ${value}`,
		valueMustBeMax: value => `Value must be less than or equal to ${value}`,
		lengthUnderMin: value => `Value must be at least ${value} characters`,
		lengthExceedsMax: value => `Value must be ${value} characters or less`,
		invalidNumber: () => "Invalid Number",
		invalidWholeNumber: () => "Invalid whole number",
		valueAlreadyExists: value => `'${value}' already exists`
	};
}